import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { take } from 'rxjs';

import { LandingContactRequestPayload, LandingContactService } from './landing-contact.service';
import { PricingCartDrawerComponent } from './pricing-cart-drawer.component';
import { BillingPlanKey } from './pricing-cart.models';
import { PricingCartService } from './pricing-cart.service';
import { LEGAL_COMPANY_NAME, LEGAL_HOSTING_PROVIDER, PUBLIC_LEGAL_URLS } from '../../core/legal/public-legal.constants';

type CapabilityCard = {
  title: string;
  description: string;
  featured: boolean;
};

type TransformationCard = {
  label: string;
  title: string;
  description: string;
  tone: 'before' | 'transformation' | 'after';
};

type PremiumPipelineStep = {
  index: string;
  label: string;
  featured?: boolean;
};

type AuditProofResultBlock = {
  title: string;
  items?: string[];
  value?: string;
  tone: 'covered' | 'missing' | 'risk';
};

type ProblemPoint = {
  title: string;
  description: string;
};

type ProductWorkflowModule = {
  title: string;
  input: string;
  action: string;
  output: string;
  featured?: boolean;
};

type IntegrationCard = {
  name: string;
  category: string;
  description: string;
  bullets: string[];
  ctaLabel: string;
  action: 'jira' | 'squash' | 'gitlab';
};

const CUSTOM_TEST_AUTOMATION_SUBJECT = 'Custom support - Test automation';
const CUSTOM_TEST_AUTOMATION_OFFER = 'custom_test_automation_consulting';
const CUSTOM_TEST_AUTOMATION_PRICE = 2000;

function nonNegativeIntegerValidator(control: AbstractControl): ValidationErrors | null {
  const numericValue = Number(control.value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return { nonNegativeInteger: true };
  }

  return null;
}

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, PricingCartDrawerComponent],
  templateUrl: './landing.component.html'
})
export class LandingComponent {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly landingContactService = inject(LandingContactService);
  readonly pricingCart = inject(PricingCartService);
  readonly privacyPolicyUrl = PUBLIC_LEGAL_URLS.privacyPolicy;
  readonly termsOfServiceUrl = PUBLIC_LEGAL_URLS.termsOfService;
  readonly legalCompanyName = LEGAL_COMPANY_NAME;
  readonly legalHostingProvider = LEGAL_HOSTING_PROVIDER;

  @ViewChild('pricingSection') private pricingSection?: ElementRef<HTMLElement>;
  @ViewChild('contactSection') private contactSection?: ElementRef<HTMLElement>;

  readonly contactForm = this.fb.nonNullable.group({
    subject: ['', [Validators.maxLength(160)]],
    firstName: ['', [Validators.required, Validators.maxLength(80)]],
    lastName: ['', [Validators.required, Validators.maxLength(80)]],
    companyName: ['', [Validators.required, Validators.maxLength(120)]],
    workEmail: ['', [Validators.required, Validators.email, Validators.maxLength(160)]],
    phoneNumber: ['', [Validators.maxLength(40)]],
    jobTitle: ['', [Validators.maxLength(120)]],
    requestedPlans: this.fb.group({}),
    message: ['', [Validators.maxLength(4000)]]
  });

  readonly submitting = signal(false);
  readonly submitSuccess = signal(false);
  readonly submitError = signal<string | null>(null);
  readonly cartSeatCount = computed(() => this.pricingCart.totalSeats());
  readonly pricingPlans = this.pricingCart.plans;
  readonly hasSelfServePlans = this.pricingCart.hasSelfServePlans;
  readonly hasSalesAssistedPlans = this.pricingCart.hasSalesAssistedPlans;
  readonly pricingSectionTitle = computed(() => 'Choose the plan that fits your QA maturity.');
  readonly pricingSectionCopy = computed(() => 'Progress from Starter to Builder, Guardian and CI/CD as your team moves from scenario creation to governed merge request decisions.');
  readonly commercialCtaTitle = computed(() => 'Ready to turn QA uncertainty into reliable delivery decisions?');
  readonly commercialCtaCopy = computed(() => 'Build your seat mix or talk to sales about rollout, enterprise setup and repository-specific automation.');
  readonly commercialStatusLabel = computed(() => {
    if (this.hasSelfServePlans() && this.hasSalesAssistedPlans()) {
      return 'Mixed rollout';
    }

    if (this.hasSelfServePlans()) {
      return 'Checkout ready';
    }

    if (this.hasSalesAssistedPlans()) {
      return 'Sales-assisted';
    }

    return 'Configuration needed';
  });

  private readonly requestedSection = signal<'pricing' | 'contact' | null>(null);
  private viewInitialized = false;

  readonly heroPreviewSignals = [
    'Requirement ambiguity detected',
    'Cart total calculation affected',
    'Missing coverage on quantity update',
    '2 scenarios to add'
  ];

  readonly heroPreviewChangeItems = [
    'Cart behavior updated',
    'Quantity update logic modified'
  ];

  readonly squashTmHighlights = [
    'Import requirements and test cases from Squash TM',
    'Audit scripted tests against business coverage',
    'Generate new Gherkin scenarios from your Squash TM context',
    'Improve existing test cases with AI assistance',
    'Push results back to keep your test repository aligned'
  ];

  readonly jiraHighlights = [
    'Import Jira issues and user stories as QA context',
    'Challenge requirements before scenario generation',
    'Generate scenarios from selected Jira stories',
    'Create Jira tickets from missing coverage or QA risks',
    'Keep product, QA and delivery teams aligned'
  ];

  readonly gitLabHighlights = [
    'Analyze merge requests before approval',
    'Detect QA impact from code changes',
    'Identify missing or weak scenario coverage',
    'Generate QA review recommendations before merge'
  ];

  readonly cicdReviewBullets = [
    'Analyze merge request impact before approval.',
    'Detect weak or missing Gherkin coverage.',
    'Identify QA risks introduced by code changes.',
    'Generate actionable review decisions.',
    'Publish or reuse review outputs in delivery workflows.'
  ];

  readonly challengeRequirementsBullets = [
    'Detect ambiguity before scenarios are written.',
    'Challenge missing business rules and acceptance criteria.',
    'Separate assumptions from observable expected outcomes.',
    'Expose edge cases, limits and rejection rules early.',
    'Turn weak requirement text into QA-ready questions.'
  ];

  readonly challengeRequirementSignals = [
    { label: 'Ambiguity', value: 'Actors, dates or limits missing' },
    { label: 'Evidence gap', value: 'Expected outcome is not measurable' },
    { label: 'QA action', value: 'Clarify before scenario generation' }
  ];

  readonly heroPreviewActionScenario = [
    'Scenario: Update cart total when quantity changes',
    'Given the user has 1 "Laptop" priced at 1000€ in the cart',
    'When the user updates the quantity to 2',
    'Then the cart displays 2 "Laptop"',
    'And the total price is 2000€'
  ].join('\n');

  readonly premiumPipelineSteps: PremiumPipelineStep[] = [
    { index: '01', label: 'Requirements' },
    { index: '02', label: 'Challenge' },
    { index: '03', label: 'Scenarios' },
    { index: '04', label: 'Automation' },
    { index: '05', label: 'Audit' },
    { index: '06', label: 'CI/CD', featured: true }
  ];

  readonly problemPoints: ProblemPoint[] = [
    {
      title: 'Vague requirements',
      description: 'Product intent is often too ambiguous to become reliable QA evidence.'
    },
    {
      title: 'Fragile Gherkin scenarios',
      description: 'Scenarios pass while still missing exact data, observable assertions or state transitions.'
    },
    {
      title: 'Missing test coverage',
      description: 'Critical business rules, edge cases and rejection paths remain invisible until late delivery.'
    },
    {
      title: 'Invisible merge request risk',
      description: 'Code changes can alter behavior before QA understands which scenarios are weak or missing.'
    },
    {
      title: 'Disconnected QA tools',
      description: 'Requirements, test repositories, tickets and merge requests rarely speak the same QA language.'
    }
  ];

  readonly productWorkflowModules: ProductWorkflowModule[] = [
    {
      title: 'Challenge requirements',
      input: 'Jira story, MR description or functional requirement',
      action: 'Detects ambiguity, missing acceptance criteria and untestable outcomes.',
      output: 'QA-ready clarification questions and requirement readiness signals',
      featured: true
    },
    {
      title: 'Generate scenarios',
      input: 'Validated product behavior and business rules',
      action: 'Creates structured, business-readable Gherkin scenarios.',
      output: 'Scenario candidates with clearer Given / When / Then evidence'
    },
    {
      title: 'Improve scenarios',
      input: 'Existing weak or incomplete Gherkin scenarios',
      action: 'Strengthens data, actions, assertions and deterministic outcomes.',
      output: 'Automation-ready scenarios without implementation coupling'
    },
    {
      title: 'Audit coverage',
      input: 'Feature expectations and existing scenario suite',
      action: 'Compares expected behavior with tested behavior and risk signals.',
      output: 'Coverage gaps, weak evidence and prioritized QA risks',
      featured: true
    },
    {
      title: 'CI/CD Review',
      input: 'GitLab merge request, changed files and QA context',
      action: 'Analyzes behavioral impact, missing coverage and deterministic scenario quality.',
      output: 'Actionable merge recommendation before approval',
      featured: true
    }
  ];

  readonly integrationCards: IntegrationCard[] = [
    {
      name: 'Jira',
      category: 'Product and requirements',
      description: 'Bring product stories into QA analysis before scenarios are generated.',
      bullets: [
        'Import user stories as requirement context',
        'Challenge requirements from Jira issues',
        'Generate scenarios from selected Jira stories',
        'Create follow-up tickets for gaps or risks'
      ],
      ctaLabel: 'Connect Jira',
      action: 'jira'
    },
    {
      name: 'Squash TM',
      category: 'Test repository',
      description: 'Reuse and improve your existing requirement and test assets.',
      bullets: [
        'Import requirements and test cases',
        'Audit existing test assets',
        'Improve scripted tests with AI assistance',
        'Push improved scenarios back'
      ],
      ctaLabel: 'Manage Squash TM',
      action: 'squash'
    },
    {
      name: 'GitLab',
      category: 'CI/CD Review',
      description: 'Connect merge request changes to QA impact before approval.',
      bullets: [
        'Analyze merge requests',
        'Detect QA impact from code changes',
        'Identify missing or weak coverage',
        'Generate QA review recommendations before merge'
      ],
      ctaLabel: 'Open GitLab setup',
      action: 'gitlab'
    }
  ];

  readonly afterScenarioContent = [
    'Scenario: Update cart total when quantity changes',
    'Given the user has 1 "Laptop" priced at 1000€ in the cart',
    'When the user updates the quantity to 2',
    'Then the cart displays 2 "Laptop"',
    'And the total price is 2000€'
  ].join('\n');

  readonly scenarioProofBefore = [
    'Scenario: Add product to cart',
    'Given the user adds a product',
    'Then the cart is updated'
  ].join('\n');

  readonly scenarioProofAfter = [
    'Scenario: Add a product to the cart and reflect item count, line item, total, and badge',
    '  Given the user is viewing the product page for "Widget Pro" with a price of 29.99, stock 10, and the shopping cart is empty',
    '  When the user clicks the "Add to cart" button for "Widget Pro"',
    '  Then the cart item count is 1',
    '  And the cart contains a line item for "Widget Pro" with unit price 29.99 and line total 29.99',
    '  And the cart total is 29.99',
    '  And the cart badge shows 1 item'
  ].join('\n');

  readonly scenarioProofBenefits = [
    'Clearer actor and object',
    'Explicit user action',
    'Stronger assertions',
    'Deterministic expected result'
  ];

  readonly auditProofRules = [
    'Products can be added to cart',
    'Quantity updates must update total price',
    'Removing a product updates the cart total',
    'Total must reflect all items and quantities'
  ];

  readonly auditProofBlocks: AuditProofResultBlock[] = [
    {
      title: 'Covered',
      items: ['Add product to cart'],
      tone: 'covered'
    },
    {
      title: 'Missing',
      items: ['Update quantity recalculates total', 'Remove product updates total'],
      tone: 'missing'
    },
    {
      title: 'Risk level',
      value: 'High on cart pricing logic',
      tone: 'risk'
    }
  ];

  readonly customTestAutomationItems = [
    'Repository and project architecture analysis',
    'Identification of critical functional journeys',
    'Test generation strategy definition',
    'Adaptation to the project technical conventions',
    'Integration recommendations for the existing workflow',
    'Custom support'
  ];

  readonly capabilityCards: CapabilityCard[] = [
    {
      title: 'Challenge requirements before scenarios',
      description: 'Detect ambiguity, missing acceptance criteria and untestable business rules before writing Gherkin.',
      featured: true
    },
    {
      title: 'Generate business-readable Gherkin',
      description: 'Create clean scenarios from challenged requirements, user stories, or existing scenario material.',
      featured: true
    },
    {
      title: 'Audit coverage and risk',
      description: 'Identify weak assertions, missing risks, redundant scenarios, and low-confidence coverage.',
      featured: false
    },
    {
      title: 'Improve existing scenarios',
      description: 'Rewrite scenarios without changing their business intent or introducing implementation details.',
      featured: false
    },
    {
      title: 'Connect QA decisions to Squash TM',
      description: 'Reuse existing Squash TM assets and synchronize improved scenarios back to the test repository.',
      featured: false
    },
    {
      title: 'CI/CD Review for Merge Requests',
      description: 'Analyze merge request impact before approval. Detect weak coverage and identify QA risks introduced by code changes.',
      featured: true
    }
  ];

  readonly transformationCards: TransformationCard[] = [
    {
      label: 'Before',
      title: 'Unreliable QA inputs',
      description: 'Ambiguous requirements, incomplete scenarios, weak assertions and missed edge cases make QA harder to trust.',
      tone: 'before'
    },
    {
      label: 'Transformation',
      title: 'Requirement challenge and deterministic generation',
      description: 'The platform challenges requirements first, then transforms behavior, risk and intent into structured scenarios, stronger test assets and decision-ready QA output.',
      tone: 'transformation'
    },
    {
      label: 'After',
      title: 'Clear QA decisions',
      description: 'Get reliable scenarios, implementation-ready automation, audit signals and merge request impact guidance.',
      tone: 'after'
    }
  ];
  readonly salesContactTitle = computed(() => 'Talk to sales about rollout or enterprise setup');
  readonly salesContactCopy = computed(() => 'Use this form if you need help choosing plans, planning rollout, or discussing enterprise purchasing.');

  constructor() {
    effect(() => {
      this.syncRequestedPlanControls();
    });

    this.contactForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.submitSuccess()) {
          this.submitSuccess.set(false);
        }

        if (this.submitError()) {
          this.submitError.set(null);
        }
      });

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const subject = String(params.get('subject') || '').trim();
        if (subject) {
          this.contactForm.patchValue({ subject }, { emitEvent: false });
        }
      });

    this.route.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        const focusSection = data['focusSection'] === 'pricing' || data['focusSection'] === 'contact'
          ? data['focusSection']
          : null;
        this.requestedSection.set(focusSection);

        if (focusSection === 'contact') {
          this.syncContactFormFromCart();
        }

        queueMicrotask(() => this.scrollToRequestedSection());
      });

    this.syncContactFormFromCart();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.scrollToRequestedSection();
  }

  navigateToReview(): void {
    void this.router.navigate(['/app/ci-cd/review']);
  }

  navigateToAudit(): void {
    void this.router.navigate(['/app/audit/workspace']);
  }

  navigateToChallengeRequirements(): void {
    void this.router.navigate(['/app/audit/challenge']);
  }

  navigateToImprove(): void {
    void this.router.navigate(['/app/builder/improve']);
  }

  navigateToGenerate(): void {
    void this.router.navigate(['/app/builder/generate']);
  }

  navigateToConnections(): void {
    void this.router.navigate(['/app/integrations']);
  }

  navigateToJiraIntegration(): void {
    void this.router.navigate(['/app/integrations/jira']);
  }

  navigateToGitLabIntegration(): void {
    void this.router.navigate(['/app/integrations/gitlab']);
  }

  navigateToSquashIntegration(): void {
    void this.router.navigate(['/app/integrations/squash']);
  }

  navigateToIntegration(action: IntegrationCard['action']): void {
    if (action === 'jira') {
      this.navigateToJiraIntegration();
      return;
    }

    if (action === 'gitlab') {
      this.navigateToGitLabIntegration();
      return;
    }

    this.navigateToSquashIntegration();
  }

  scrollToPricing(): void {
    this.pricingSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  scrollToContact(): void {
    this.contactSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  navigateToPricing(): void {
    if (this.router.url.startsWith('/pricing')) {
      this.scrollToPricing();
      return;
    }

    void this.router.navigate(['/pricing']);
  }

  talkToSales(planKey?: BillingPlanKey): void {
    if (planKey) {
      const plan = this.pricingPlans().find((candidate) => candidate.key === planKey);
      if (plan?.salesOnly) {
        this.requestedPlanGroup.get(planKey)?.setValue(
          Math.max(1, Number(this.requestedPlanGroup.get(planKey)?.value || 0)),
          { emitEvent: false }
        );
      } else {
        this.pricingCart.ensurePlan(planKey);
      }
    }

    this.syncContactFormFromCart(true);

    if (this.router.url.startsWith('/contact')) {
      this.scrollToContact();
      return;
    }

    void this.router.navigate(['/contact']);
  }

  requestCustomTestAutomationConsulting(): void {
    this.contactForm.patchValue(
      {
        subject: CUSTOM_TEST_AUTOMATION_SUBJECT,
        message: [
          CUSTOM_TEST_AUTOMATION_SUBJECT,
          '',
          `Offer: ${CUSTOM_TEST_AUTOMATION_OFFER}`,
          `Budget indication: ${CUSTOM_TEST_AUTOMATION_PRICE}€`,
          '',
          'I would like to discuss a custom test automation engagement based on my repository, architecture, and critical business flows.'
        ].join('\n')
      },
      { emitEvent: false }
    );

    void this.router.navigate(['/contact'], {
      queryParams: {
        subject: CUSTOM_TEST_AUTOMATION_SUBJECT,
        offer: CUSTOM_TEST_AUTOMATION_OFFER,
        price: CUSTOM_TEST_AUTOMATION_PRICE
      }
    });
  }

  addPlanAndOpenCart(planKey: BillingPlanKey): void {
    this.pricingCart.ensurePlan(planKey);
    this.pricingCart.openDrawer();
  }

  updatePlanQuantity(planKey: BillingPlanKey, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.pricingCart.setQuantity(planKey, target?.value ?? 0);
  }

  incrementPlan(planKey: BillingPlanKey): void {
    this.pricingCart.increment(planKey);
  }

  decrementPlan(planKey: BillingPlanKey): void {
    this.pricingCart.decrement(planKey);
  }

  openCartDrawer(): void {
    this.pricingCart.openDrawer();
  }

  cartQuantity(planKey: BillingPlanKey): number {
    return this.pricingCart.getQuantity(planKey);
  }

  planMonthlyTotal(planKey: BillingPlanKey): string {
    return `${this.pricingCart.formatAmount(this.pricingCart.getPlanTotal(planKey))} / month`;
  }

  submitContactRequest(): void {
    if (this.submitting()) {
      return;
    }

    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      this.scrollToContact();
      return;
    }

    this.submitting.set(true);
    this.submitError.set(null);

    const rawValue = this.contactForm.getRawValue();
    const rawRequestedPlans = (rawValue.requestedPlans || {}) as Record<string, number>;
    const payload: LandingContactRequestPayload = {
      subject: rawValue.subject.trim(),
      firstName: rawValue.firstName.trim(),
      lastName: rawValue.lastName.trim(),
      companyName: rawValue.companyName.trim(),
      workEmail: rawValue.workEmail.trim(),
      phoneNumber: rawValue.phoneNumber.trim(),
      jobTitle: rawValue.jobTitle.trim(),
      requestedPlans: this.pricingPlans()
        .map((plan) => ({
          planKey: plan.key,
          planName: plan.name,
          quantity: Number(rawRequestedPlans[plan.key] || 0),
        }))
        .filter((plan) => plan.quantity > 0),
      message: rawValue.message.trim()
    };

    this.landingContactService.submitPricingRequest(payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.submitSuccess.set(true);
          this.contactForm.reset({
            subject: '',
            firstName: '',
            lastName: '',
            companyName: '',
            workEmail: '',
            phoneNumber: '',
            jobTitle: '',
            message: ''
          });
          this.syncContactFormFromCart(true);
          this.contactForm.markAsPristine();
          this.contactForm.markAsUntouched();
        },
        error: () => {
          this.submitting.set(false);
          this.submitError.set('Unable to send your request right now. Please try again in a moment.');
        }
      });
  }

  fieldError(controlName: string): string | null {
    const control = this.contactForm.get(controlName);

    if (!control || !control.errors || (!control.touched && !control.dirty)) {
      return null;
    }

    if (control.errors['required']) {
      return 'This field is required.';
    }

    if (control.errors['email']) {
      return 'Enter a valid work email.';
    }

    if (control.errors['maxlength']) {
      return 'This value is too long.';
    }

    if (control.errors['nonNegativeInteger']) {
      return 'Enter a whole number greater than or equal to 0.';
    }

    return 'Check this value and try again.';
  }

  get requestedPlanGroup(): FormGroup {
    return this.contactForm.get('requestedPlans') as FormGroup;
  }

  requestedPlanControl(planKey: string): FormControl<number> {
    const existingControl = this.requestedPlanGroup.get(planKey);
    if (existingControl instanceof FormControl) {
      return existingControl as FormControl<number>;
    }

    const control = new FormControl(0, {
      nonNullable: true,
      validators: [Validators.required, nonNegativeIntegerValidator]
    });
    this.requestedPlanGroup.addControl(planKey, control);
    return control;
  }

  private scrollToRequestedSection(): void {
    if (!this.viewInitialized) {
      return;
    }

    const focusSection = this.requestedSection();
    if (focusSection === 'pricing') {
      this.scrollToPricing();
      return;
    }

    if (focusSection === 'contact') {
      this.scrollToContact();
    }
  }

  private syncContactFormFromCart(force = false): void {
    if (!force && this.contactForm.dirty) {
      return;
    }

    const requestedPlanValues = Object.fromEntries(
      this.pricingPlans().map((plan) => [
        plan.key,
        plan.salesOnly
          ? Number(this.requestedPlanGroup.get(plan.key)?.value || 0)
          : this.pricingCart.getQuantity(plan.key),
      ])
    );
    this.requestedPlanGroup.patchValue(requestedPlanValues, { emitEvent: false });
  }

  private syncRequestedPlanControls(): void {
    const planKeys = this.pricingPlans().map((plan) => plan.key);
    const existingPlanKeys = Object.keys(this.requestedPlanGroup.controls);

    for (const planKey of planKeys) {
      if (!this.requestedPlanGroup.contains(planKey)) {
        this.requestedPlanGroup.addControl(
          planKey,
          new FormControl(0, {
            nonNullable: true,
            validators: [Validators.required, nonNegativeIntegerValidator]
          })
        );
      }
    }

    for (const planKey of existingPlanKeys) {
      if (!planKeys.includes(planKey)) {
        this.requestedPlanGroup.removeControl(planKey);
      }
    }
  }
}
